// src/components/ui/BoutonCloche.tsx — SCE Issues
// Apparence ET comportement communs des cloches « Suivre » : un problème
// (Lot 12), un projet (Lot 12 bis), un équipement (Lot 12 ter). Icône seule
// depuis le 26/09 (« trop gros ») — même gabarit que « Rafraîchir » : 44 px de
// cible, sans cadre. Suivi = cloche qui sonne sur un disque gris ; le nombre
// d'abonnés en petite pastille ; le mot (« Suivre », « Suivi · 2 personnes »)
// reste dans l'infobulle et l'aria-label.
//
// Trois états, les mêmes sur les trois fiches (30/09) :
//  - en attente (`indisponible`, on ne sait pas encore si l'on suit) : cloche
//    atténuée et inactive — jamais « non suivi » par défaut, sinon elle bascule
//    en « suivi » à l'ouverture, à un moment différent selon la fiche ;
//  - non suivi : cloche vide ; suivi : cloche qui sonne sur un disque gris.
// Fondu de 0,15 s entre les états. La cloche SONNE une fois quand on vient de
// la suivre — jamais à l'ouverture de la fiche. `enCours` (écriture) atténue
// aussi le bouton.

import { useEffect, useRef, useState } from 'react'
import { Bell, BellRing } from 'lucide-react'

export function BoutonCloche({
  suis,
  total,
  enCours,
  indisponible,
  lectureSeule,
  ariaLabel,
  titre,
  onClick,
}: {
  suis: boolean
  /** Nombre d'abonnés, affiché s'il est connu et non nul. */
  total?: number
  /** Une écriture est en route. */
  enCours: boolean
  /** L'état de la personne n'est pas encore lu. */
  indisponible?: boolean
  lectureSeule: boolean
  ariaLabel: string
  titre: string
  onClick: () => void
}): JSX.Element {
  const [sonne, setSonne] = useState(false)
  const clic = useRef(false)
  const suisAvant = useRef(suis)

  // Sonne seulement si le passage à « suivi » suit un clic de la personne.
  useEffect(() => {
    if (!suisAvant.current && suis && clic.current) setSonne(true)
    suisAvant.current = suis
    if (!enCours) clic.current = false
  }, [suis, enCours])

  return (
    <button
      type="button"
      onClick={() => {
        clic.current = true
        onClick()
      }}
      disabled={lectureSeule || enCours || !!indisponible}
      aria-pressed={suis}
      aria-label={ariaLabel}
      title={titre}
      className="relative flex items-center justify-center"
      style={{
        width: 44,
        height: 44,
        borderRadius: 22,
        border: 'none',
        background: suis ? 'var(--color-gray-10)' : 'transparent',
        color: 'var(--color-black)',
        cursor: lectureSeule ? 'default' : 'pointer',
        opacity: enCours || indisponible ? 0.5 : 1,
        flexShrink: 0,
        transition: 'background-color 0.15s ease, opacity 0.15s ease',
      }}
    >
      <span
        className={sonne ? 'cloche-sonne' : undefined}
        onAnimationEnd={() => setSonne(false)}
        style={{ display: 'inline-flex' }}
      >
        {suis ? <BellRing size={18} aria-hidden="true" /> : <Bell size={18} aria-hidden="true" />}
      </span>
      {!!total && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 5,
            right: 3,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 8,
            background: 'var(--color-gray-90)',
            color: 'var(--color-white)',
            fontSize: 10,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {total}
        </span>
      )}
    </button>
  )
}
